import { clsx, type ClassValue } from "clsx";

import { createContext, useContext } from "react";
import { Tabs as RadixTabs } from "radix-ui";

import { twMerge } from "@/design-system/twMerge";

type TabsSize = "regular" | "small";

const TabsSizeContext = createContext<TabsSize>("regular");

export function tabListClassnames(overrides?: ClassValue) {
  return twMerge(clsx("", overrides));
}
export function tabTriggerClassnames(
  overrides?: ClassValue,
  size: TabsSize = "regular",
) {
  return twMerge(
    clsx(
      'px-4 py-3 cursor-pointer text-textSubtle data-[state="active"]:text-textActionSelected transition-all rounded-xs border-b-2 border-transparent data-[state="active"]:border-textActionSelected disabled:opacity-50 disabled:cursor-not-allowed',
      size === "small" ? "text-heading-xs" : "text-heading-md",
      overrides,
    ),
  );
}
export function tabContentClassnames(overrides?: ClassValue) {
  return twMerge(clsx(overrides));
}

function List({ className, ...props }: RadixTabs.TabsListProps) {
  return <RadixTabs.List className={tabListClassnames(className)} {...props} />;
}

function Trigger({ className, ...props }: RadixTabs.TabsTriggerProps) {
  const size = useContext(TabsSizeContext);
  return (
    <RadixTabs.Trigger
      className={tabTriggerClassnames(className, size)}
      {...props}
    />
  );
}

function Content({ className, ...props }: RadixTabs.TabsContentProps) {
  return (
    <RadixTabs.Content className={tabContentClassnames(className)} {...props} />
  );
}

interface RootProps extends RadixTabs.TabsProps {
  size?: TabsSize;
}

function Root({ size = "regular", ...props }: RootProps) {
  return (
    <TabsSizeContext.Provider value={size}>
      <RadixTabs.Root {...props} />
    </TabsSizeContext.Provider>
  );
}

export const Tabs = {
  Root,
  List,
  Trigger,
  Content,
};
